import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, ChevronLeft, ChevronRight, Maximize2, Minimize2, Captions, Loader2, RotateCcw, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useFullscreen } from "@/lib/useFullscreen";
import { fetchVoice, SIMRAN } from "@/lib/aiot";
import { SlideVisual } from "@/components/aiot/SlideVisual";
import { Check, Activity } from "@/components/aiot/SlideTasks";

const useVoice = (lec, idx) => {
  const audio = useRef(null);
  const cache = useRef({});
  const [v, setV] = useState({ marks: [], ready: false, err: false, idx: -1 });
  const get = useCallback((n) => (cache.current[n] ||= fetchVoice(lec.id, n, lec.lang).catch(() => null)), [lec.id, lec.lang]);
  useEffect(() => { cache.current = {}; }, [lec.id, lec.lang]);
  useEffect(() => {
    let live = true;
    const a = audio.current;
    a.pause();
    setV({ marks: [], ready: false, err: false, idx });
    get(idx).then((r) => {
      if (!live) return;
      if (!r) { setV({ marks: [], ready: false, err: true, idx }); return; }
      a.src = `data:audio/mpeg;base64,${r.audio}`;
      setV({ marks: r.marks, ready: true, err: false, idx });
    });
    if (idx + 1 < lec.slides.length) get(idx + 1);
    return () => { live = false; };
  }, [idx, get, lec.slides.length]);
  return { audio, ...v, ready: v.ready && v.idx === idx };
};

const Body = ({ s, idx, total, picked, onPick, onNext }) => {
  const { L } = useLang();
  const tag = s.kind === "check" ? L("Quick check", "क्विक चेक") : s.kind === "activity" ? L("Activity", "एक्टिविटी") : L("Slide", "स्लाइड");
  return (
    <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}
      className="w-full p-5 sm:p-9 grid lg:grid-cols-[1.05fr_1fr] gap-7 items-center" data-testid={`lecture-slide-${idx}`}>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F766E]">{tag} · {idx + 1}/{total}</div>
        <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0B1F3A] leading-tight" data-testid="lecture-slide-title">{s.title}</h3>
        {s.kind === "check" && <Check s={s} picked={picked} onPick={onPick} onNext={onNext} />}
        {s.kind === "activity" && <Activity s={s} onNext={onNext} />}
        {s.kind === "slide" && (
          <ul className="mt-5 space-y-2.5">
            {s.bullets.map((b, i) => (
              <motion.li key={`${i}-${b}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.12 }} className="flex gap-3 text-[15px] text-slate-700 leading-snug">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#14B8A6] shrink-0" />{b}
              </motion.li>
            ))}
          </ul>
        )}
      </div>
      <div className="min-w-0">{s.visual && <SlideVisual v={s.visual} />}</div>
    </motion.div>
  );
};

const CaptionBar = ({ s, marks, t, err, speaking, show }) => {
  const { L } = useLang();
  const cur = [...marks].reverse().find((m) => t >= m[0]) || marks[0];
  const text = err ? s.say || s.title : cur?.[2] || "";
  return (
    <div className="flex items-center gap-3 bg-white border-t border-slate-200 px-4 py-3 min-h-[68px]" data-testid="lecture-captions">
      <span className="relative shrink-0 w-11 h-11">
        {speaking && <span className="absolute -inset-1 rounded-full border-2 border-[#14B8A6] animate-ping opacity-60" />}
        <img src={SIMRAN[1]} alt="Simran Ma'am" className="relative w-11 h-11 rounded-full object-cover object-[50%_25%] ring-2 ring-white" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold text-[#0F766E]">Simran Ma&apos;am {err && <span className="text-[#B45309] font-semibold">· {L("voice is busy, read along", "आवाज़ अभी व्यस्त है, पढ़ते रहिए")}</span>}</div>
        {show && <p className={`text-sm text-[#0B1F3A] leading-snug ${err ? "max-h-20 overflow-y-auto" : "line-clamp-2"}`} data-testid="lecture-caption-text">{text}</p>}
      </div>
    </div>
  );
};

const Controls = ({ idx, total, playing, loading, onPlay, go, onNext, cc, setCc, full, toggle }) => {
  const { L } = useLang();
  const btn = "w-10 h-10 rounded-full grid place-items-center text-[#0B1F3A] hover:bg-slate-100 transition-colors disabled:opacity-30";
  return (
    <div className="bg-white border-t border-slate-100 px-3 py-2">
      <div className="flex gap-1 mb-2" data-testid="lecture-progress">
        {Array.from({ length: total }, (_, i) => <button key={i} type="button" onClick={() => go(i)} aria-label={`Slide ${i + 1}`} className={`h-1.5 flex-1 rounded-full transition-colors ${i < idx ? "bg-[#14B8A6]" : i === idx ? "bg-[#0F766E]" : "bg-slate-200"}`} data-testid={`lecture-dot-${i}`} />)}
      </div>
      <div className="flex items-center gap-1">
        <button type="button" className={btn} onClick={() => go(idx - 1)} disabled={idx === 0} aria-label="Previous" data-testid="lecture-prev"><ChevronLeft className="w-5 h-5" /></button>
        <button type="button" onClick={onPlay} className="w-12 h-12 rounded-full grid place-items-center bg-[#0F766E] text-white hover:bg-[#0B1F3A] transition-colors" aria-label={playing ? "Pause" : "Play"} data-testid="lecture-play">
          {loading && playing ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <button type="button" className={btn} onClick={onNext} aria-label="Next" data-testid="lecture-next"><ChevronRight className="w-5 h-5" /></button>
        <span className="ml-2 text-xs font-semibold text-slate-500" data-testid="lecture-counter">{idx + 1} / {total}</span>
        <span className="flex-1" />
        <button type="button" className={`${btn} ${cc ? "text-[#0F766E]" : ""}`} onClick={() => setCc(!cc)} aria-label={L("Captions", "कैप्शन")} data-testid="lecture-cc"><Captions className="w-5 h-5" /></button>
        <button type="button" className={btn} onClick={toggle} aria-label={full ? "Exit full screen" : "Full screen"} data-testid="lecture-fullscreen">{full ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</button>
      </div>
    </div>
  );
};

const Finished = ({ onReplay, next }) => {
  const { L } = useLang();
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 z-10 bg-white/95 backdrop-blur grid place-items-center text-center p-6" data-testid="lecture-finished">
      <div>
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#059669]" />
        <h3 className="mt-3 text-2xl font-extrabold text-[#0B1F3A]">{L("Lecture complete", "लेक्चर पूरा हुआ")}</h3>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={onReplay} className="rounded-full border border-slate-300 px-5 h-11 inline-flex items-center gap-2 font-bold text-[#0B1F3A]" data-testid="lecture-replay"><RotateCcw className="w-4 h-4" />{L("Replay", "फिर से देखें")}</button>
          {next}
        </div>
      </div>
    </motion.div>
  );
};

export const LecturePlayer = ({ lec, onComplete, next }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [cc, setCc] = useState(true);
  const [picks, setPicks] = useState({});
  const [done, setDone] = useState(false);
  const box = useRef(null);
  const { full, toggle } = useFullscreen(box);
  const { audio, marks, ready, err } = useVoice(lec, idx);
  const total = lec.slides.length;
  const s = lec.slides[idx];

  useEffect(() => { setIdx(0); setPlaying(false); setPicks({}); setDone(false); }, [lec.id, lec.lang]);
  useEffect(() => { setT(0); }, [idx]);
  useEffect(() => {
    const a = audio.current;
    if (!ready) return;
    if (playing) a.play().catch((e) => e?.name !== "AbortError" && setPlaying(false)); else a.pause();
  }, [playing, ready, idx, audio]);
  useEffect(() => () => audio.current?.pause(), [audio]);

  const go = (i) => { if (i >= 0 && i < total) { setIdx(i); setDone(false); } };
  const finish = () => { setPlaying(false); setDone(true); onComplete?.(); };
  const advance = () => (idx < total - 1 ? go(idx + 1) : finish());
  const ended = () => { if (s.kind === "slide") advance(); else setPlaying(false); };

  return (
    <div ref={box} className={`relative bg-[#F8FAFC] flex flex-col ${full ? "fixed inset-0 z-[100] h-full" : "rounded-3xl border border-slate-200 overflow-hidden shadow-[0_30px_80px_-50px_rgba(11,31,58,0.45)]"}`} data-testid="lecture-player">
      <audio ref={audio} onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onEnded={ended} />
      <div className="relative flex-1 flex items-center min-h-[440px] lg:min-h-[480px] overflow-y-auto" style={{ backgroundImage: "radial-gradient(#E2E8F0 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
        <AnimatePresence mode="wait">
          <Body key={idx} s={s} idx={idx} total={total} picked={picks[idx]} onPick={(i) => setPicks({ ...picks, [idx]: i })} onNext={() => { setPlaying(true); advance(); }} />
        </AnimatePresence>
        {done && <Finished onReplay={() => { go(0); setPlaying(true); }} next={next} />}
      </div>
      <CaptionBar s={s} marks={marks} t={t} err={err} speaking={playing && ready} show={cc} />
      <Controls idx={idx} total={total} playing={playing} loading={!ready && !err} onPlay={() => setPlaying(!playing)} go={go} onNext={advance} cc={cc} setCc={setCc} full={full} toggle={toggle} />
    </div>
  );
};
